import { useCallback, useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { getStoredAuthToken } from "../../services/authStorage";
import { acquirePersistentMicrophone } from "../../services/persistentMicrophone";
import { emitSocketVerificationOnce } from "../../services/socketVerification";
import { downsampleToInt16, getAudioContextConstructor, toArrayBuffer } from "../TournamentVoiceChat";

// Voz y chat del Campeonato: el mismo audio de las partidas online (PCM 16 kHz
// reenviado por el servidor) en la sala "championship-live".
const EVENTS = {
  join: "CHAMP_LIVE_JOIN",
  leave: "CHAMP_LIVE_LEAVE",
  state: "CHAMP_LIVE_STATE",
  micState: "CHAMP_VOICE_STATE",
  frame: "CHAMP_VOICE_FRAME",
  adminMute: "CHAMP_VOICE_ADMIN_MUTE",
  chatSend: "CHAMP_CHAT_SEND",
  chatMessage: "CHAMP_CHAT_MESSAGE",
  error: "CHAMP_LIVE_ERROR",
};
const SAMPLE_RATE = 16000;
const BUFFER_SIZE = 2048;
const MAX_QUEUE_SECONDS = 0.42;
const SPEAKING_HOLD_MS = 600;

const getSocketUrl = () => {
  const envSocketUrl = String(process.env.REACT_APP_SOCKET_URL || "").trim();
  if (envSocketUrl) return envSocketUrl;
  if (window.location.port === "3000") return `${window.location.protocol}//${window.location.hostname}:5001`;
  return window.location.origin;
};

// Reuses the game's shared socket, or opens it the same way the lobby does.
const getSharedSocket = () => {
  if (typeof window === "undefined") return null;
  const existing = window.__ximaSocket || window.socket || null;
  if (existing) {
    if (existing.disconnected && typeof existing.connect === "function") existing.connect();
    return existing;
  }
  const socket = io(getSocketUrl(), { transports: ["websocket", "polling"], reconnection: true });
  socket.on("connect", () => emitSocketVerificationOnce(socket, getStoredAuthToken() || ""));
  window.__ximaSocket = socket;
  window.socket = socket;
  return socket;
};

const useChampionshipLive = () => {
  const socketRef = useRef(null);
  const captureRef = useRef(null);
  const leaseRef = useRef(null);
  const sequenceRef = useRef(0);
  const playbackContextRef = useRef(null);
  const scheduleRef = useRef(new Map());
  const speakingTimersRef = useRef(new Map());
  const listenMutedRef = useRef({});
  const listeningRef = useRef(false);
  const micActiveRef = useRef(false);

  const [voice, setVoice] = useState({});
  const [chat, setChat] = useState([]);
  const [me, setMe] = useState("");
  const [canSpeak, setCanSpeak] = useState(false);
  const [canChat, setCanChat] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [micActive, setMicActive] = useState(false);
  const [listening, setListening] = useState(false);
  const [listenMuted, setListenMuted] = useState({});
  const [speaking, setSpeaking] = useState({});
  const [status, setStatus] = useState("");

  const stopCapture = useCallback(() => {
    const capture = captureRef.current;
    captureRef.current = null;
    if (capture) {
      try {
        capture.processor.onaudioprocess = null;
        capture.source.disconnect();
        capture.processor.disconnect();
        capture.zeroGain.disconnect();
      } catch {
        // best-effort audio graph cleanup
      }
      if (capture.audioContext.state !== "closed") capture.audioContext.close().catch(() => {});
    }
    try {
      leaseRef.current?.release?.();
    } catch {
      // shared microphone cleanup is best-effort
    }
    leaseRef.current = null;
  }, []);

  const ensurePlayback = useCallback(async (userGesture) => {
    const AudioContextConstructor = getAudioContextConstructor();
    if (!AudioContextConstructor) return null;
    let context = playbackContextRef.current;
    if (!context || context.state === "closed") {
      try {
        context = new AudioContextConstructor({ sampleRate: SAMPLE_RATE });
      } catch {
        context = new AudioContextConstructor();
      }
      playbackContextRef.current = context;
    }
    if (context.state === "suspended" && userGesture) await context.resume().catch(() => {});
    return context;
  }, []);

  const markSpeaking = useCallback((userId) => {
    setSpeaking((prev) => (prev[userId] ? prev : { ...prev, [userId]: true }));
    window.clearTimeout(speakingTimersRef.current.get(userId));
    speakingTimersRef.current.set(
      userId,
      window.setTimeout(() => {
        setSpeaking((prev) => {
          if (!prev[userId]) return prev;
          const next = { ...prev };
          delete next[userId];
          return next;
        });
      }, SPEAKING_HOLD_MS),
    );
  }, []);

  const playFrame = useCallback(
    async (payload) => {
      const userId = String(payload?.userId || "");
      if (!userId) return;
      markSpeaking(userId);
      if (!listeningRef.current || listenMutedRef.current[userId]) return;
      const buffer = toArrayBuffer(payload?.pcm);
      if (!buffer || buffer.byteLength < 2) return;
      const context = await ensurePlayback(false);
      if (!context || context.state === "suspended") return;
      const pcm = new Int16Array(buffer);
      const sampleRate = Math.max(8000, Math.min(24000, Number(payload?.sampleRate) || SAMPLE_RATE));
      const audioBuffer = context.createBuffer(1, pcm.length, sampleRate);
      const channel = audioBuffer.getChannelData(0);
      for (let index = 0; index < pcm.length; index += 1) channel[index] = pcm[index] / 0x8000;
      const source = context.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(context.destination);
      const now = context.currentTime;
      let nextStart = Number(scheduleRef.current.get(userId) || 0);
      if (!Number.isFinite(nextStart) || nextStart < now || nextStart - now > MAX_QUEUE_SECONDS) {
        nextStart = now + 0.035;
      }
      source.start(nextStart);
      scheduleRef.current.set(userId, nextStart + audioBuffer.duration);
    },
    [ensurePlayback, markSpeaking],
  );

  useEffect(() => {
    const socket = getSharedSocket();
    socketRef.current = socket;
    if (!socket) return undefined;
    const join = () => socket.emit(EVENTS.join);
    const handleState = (payload) => {
      if (payload?.voice) setVoice(payload.voice);
      if (Array.isArray(payload?.chat)) setChat(payload.chat);
      if (typeof payload?.me === "string") setMe(payload.me);
      if (typeof payload?.canSpeak === "boolean") setCanSpeak(payload.canSpeak);
      if (typeof payload?.canChat === "boolean") setCanChat(payload.canChat);
      if (typeof payload?.isAdmin === "boolean") setIsAdmin(payload.isAdmin);
    };
    const handleChat = (message) => setChat((prev) => [...prev, message].slice(-100));
    const handleError = (payload) => setStatus(String(payload?.message || ""));
    socket.on("connect", join);
    socket.on(EVENTS.state, handleState);
    socket.on(EVENTS.chatMessage, handleChat);
    socket.on(EVENTS.frame, playFrame);
    socket.on(EVENTS.error, handleError);
    join();
    // The session check can finish after the first join: ask again for the full state.
    const rejoin = window.setTimeout(join, 2500);
    const timers = speakingTimersRef.current;
    return () => {
      window.clearTimeout(rejoin);
      socket.emit(EVENTS.leave);
      socket.off("connect", join);
      socket.off(EVENTS.state, handleState);
      socket.off(EVENTS.chatMessage, handleChat);
      socket.off(EVENTS.frame, playFrame);
      socket.off(EVENTS.error, handleError);
      timers.forEach((timer) => window.clearTimeout(timer));
      stopCapture();
      playbackContextRef.current?.close?.().catch(() => {});
      playbackContextRef.current = null;
    };
  }, [playFrame, stopCapture]);

  // The admin silenced me: the server stops my audio, and the mic goes off here too.
  const adminMutedMe = Boolean(me && voice[me]?.adminMuted);
  useEffect(() => {
    if (adminMutedMe && micActiveRef.current) {
      micActiveRef.current = false;
      setMicActive(false);
      stopCapture();
      setStatus("El administrador silenció tu micrófono.");
    }
  }, [adminMutedMe, stopCapture]);

  const startMic = useCallback(async () => {
    const socket = socketRef.current;
    const AudioContextConstructor = getAudioContextConstructor();
    if (!socket || !AudioContextConstructor || !navigator.mediaDevices?.getUserMedia) {
      setStatus("Micrófono no disponible en este navegador.");
      return;
    }
    try {
      setStatus("Permiso del micrófono…");
      const lease = await acquirePersistentMicrophone({ windowRef: window });
      leaseRef.current = lease;
      let audioContext;
      try {
        audioContext = new AudioContextConstructor({ sampleRate: SAMPLE_RATE });
      } catch {
        audioContext = new AudioContextConstructor();
      }
      await audioContext.resume().catch(() => {});
      const source = audioContext.createMediaStreamSource(lease.stream);
      const processor = audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1);
      const zeroGain = audioContext.createGain();
      zeroGain.gain.value = 0;
      processor.onaudioprocess = (event) => {
        if (!micActiveRef.current) return;
        const pcm = downsampleToInt16(event.inputBuffer.getChannelData(0), audioContext.sampleRate, SAMPLE_RATE);
        if (!pcm || pcm.byteLength <= 0) return;
        socket.emit(EVENTS.frame, {
          sampleRate: SAMPLE_RATE,
          sequence: sequenceRef.current,
          pcm: pcm.buffer.slice(pcm.byteOffset, pcm.byteOffset + pcm.byteLength),
        });
        sequenceRef.current = (sequenceRef.current + 1) % 1000000000;
      };
      source.connect(processor);
      processor.connect(zeroGain);
      zeroGain.connect(audioContext.destination);
      captureRef.current = { audioContext, source, processor, zeroGain };
      micActiveRef.current = true;
      setMicActive(true);
      setStatus("Micrófono activo.");
      socket.emit(EVENTS.micState, { micActive: true });
    } catch (error) {
      stopCapture();
      setStatus(error?.name === "NotAllowedError" ? "Permiso del micrófono rechazado." : "No se pudo activar el micrófono.");
    }
  }, [stopCapture]);

  const toggleMic = useCallback(() => {
    if (micActiveRef.current) {
      micActiveRef.current = false;
      setMicActive(false);
      stopCapture();
      socketRef.current?.emit(EVENTS.micState, { micActive: false });
      setStatus("Micrófono apagado.");
      return;
    }
    if (adminMutedMe) {
      setStatus("El administrador silenció tu micrófono.");
      return;
    }
    startMic();
  }, [adminMutedMe, startMic, stopCapture]);

  // "Escuchar" for everyone at once (the browser needs a tap to play audio).
  const toggleListening = useCallback(async () => {
    const next = !listeningRef.current;
    listeningRef.current = next;
    setListening(next);
    if (next) await ensurePlayback(true);
  }, [ensurePlayback]);

  // Escuchar o no a un jugador concreto.
  const toggleListenTo = useCallback(
    async (userId) => {
      if (!listeningRef.current) {
        listeningRef.current = true;
        setListening(true);
        await ensurePlayback(true);
        const next = { ...listenMutedRef.current, [userId]: false };
        listenMutedRef.current = next;
        setListenMuted(next);
        return;
      }
      const next = { ...listenMutedRef.current, [userId]: !listenMutedRef.current[userId] };
      listenMutedRef.current = next;
      setListenMuted(next);
    },
    [ensurePlayback],
  );

  const setAdminMute = useCallback((targetUserId, muted) => {
    socketRef.current?.emit(EVENTS.adminMute, { targetUserId, muted });
  }, []);

  // Pide de nuevo el estado (tras unirse o salir del campeonato).
  const refresh = useCallback(() => socketRef.current?.emit(EVENTS.join), []);

  const sendChat = useCallback((text) => {
    const clean = String(text || "").trim();
    if (!clean) return false;
    socketRef.current?.emit(EVENTS.chatSend, { text: clean });
    return true;
  }, []);

  return {
    voice,
    chat,
    me,
    canSpeak,
    canChat,
    isAdmin,
    micActive,
    adminMutedMe,
    listening,
    listenMuted,
    speaking,
    status,
    toggleMic,
    toggleListening,
    toggleListenTo,
    setAdminMute,
    sendChat,
    refresh,
  };
};

export default useChampionshipLive;
